// ── Smart analytical scan sweep — canvas pixel engine ────────────────────────
// Replaces the CSS-only glitch with a real canvas effect: a Sobel edge map of
// the preview is computed ONCE, then a neon-cyan "AI analysis" wake trails a
// HUD laser line as it sweeps top→bottom. Per-frame cost stays bounded because
// only the ~45px wake band is mutated (the full frame base is restored with a
// single typed-array copy). Self-stops as soon as the canvas leaves the DOM;
// reduced-motion renders a single clean still frame instead of animating.
//
// Two tiers: same-origin/blob sources run the full Sobel scan; cross-origin
// sources (e.g. GCS signed avatar URLs) can't be pixel-read without CORS, so the
// engine falls back to a plain laser + translucent cyan band that still renders
// and animates over the loaded image.

import { prefersReducedMotion } from './morph';

const MAX_DIM = 520; // working-canvas cap — keeps the pixel loop cheap
const WAKE = 45; // trailing glow height behind the laser line
const EDGE_THRESHOLD = 40; // Sobel magnitude required to light a pixel
const SCAN_SPEED = 2.5; // px per frame

export interface SweepStart {
  /** Cancel the animation loop and any pending work. */
  stop: () => void;
}

/** Downscale the natural image so its larger side fits MAX_DIM (keeps ratio). */
function fitDims(naturalW: number, naturalH: number): { width: number; height: number } {
  const scale = Math.min(1, MAX_DIM / Math.max(naturalW, naturalH));
  return {
    width: Math.max(1, Math.round(naturalW * scale)),
    height: Math.max(1, Math.round(naturalH * scale)),
  };
}

/**
 * Pure-math structural detector — Sobel convolution on luminance produces an
 * edge-magnitude map (0..inf) used to decide where the scan lights the
 * wireframe. Borders are left as 0 (the reference loop skips y/x = 0 and h-1/w-1).
 */
function generateSobelEdges(imgData: ImageData): Float32Array {
  const { width: w, height: h, data } = imgData;
  const output = new Float32Array(w * h);
  // Sobel operators for structural change along X and Y.
  const kx = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
  const ky = [-1, -2, -1, 0, 0, 0, 1, 2, 1];

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      let pixelX = 0;
      let pixelY = 0;

      for (let cy = -1; cy <= 1; cy++) {
        for (let cx = -1; cx <= 1; cx++) {
          const idx = ((y + cy) * w + (x + cx)) * 4;
          const val = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
          const kIdx = (cy + 1) * 3 + (cx + 1);
          pixelX += val * kx[kIdx];
          pixelY += val * ky[kIdx];
        }
      }

      output[y * w + x] = Math.sqrt(pixelX * pixelX + pixelY * pixelY);
    }
  }
  return output;
}

/**
 * Start the analytical sweep on a fresh canvas.
 * - `imageUrl` — a same-origin/blob preview URL (full Sobel tier) or a
 *   cross-origin signed URL (canvas is tainted, so the engine falls back to a
 *   plain laser + tint band scan — image still draws and animates).
 * - `onSized` — fired once after the canvas is sized to its working dims, so the
 *   caller can reflow the modal to the natural card size.
 */
export function startSweepScan(
  canvas: HTMLCanvasElement,
  imageUrl: string,
  onSized?: () => void,
): SweepStart {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const img = new Image();
  let raf = 0;
  let stopped = false;
  let crossOriginTried = false;

  const stop = (): void => {
    stopped = true;
    cancelAnimationFrame(raf);
  };

  // Cross-origin images need CORS for pixel reads (Sobel tier). We only request
  // it when the source isn't already same-origin/blob; if the server rejects the
  // CORS request the image won't load, so retry once without crossOrigin and let
  // the plain tier render instead.
  const isSameOrigin =
    imageUrl.startsWith('blob:') ||
    (() => {
      try {
        return new URL(imageUrl, window.location.href).origin === window.location.origin;
      } catch {
        return false;
      }
    })();
  if (!isSameOrigin) img.crossOrigin = 'anonymous';

  img.onerror = (): void => {
    if (!crossOriginTried && img.crossOrigin) {
      crossOriginTried = true;
      img.crossOrigin = '';
      img.src = imageUrl;
      return;
    }
    stop();
  };

  img.onload = (): void => {
    if (stopped || !ctx || canvas.isConnected === false) return;

    const { width, height } = fitDims(img.naturalWidth, img.naturalHeight);
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    let original: Uint8ClampedArray | null = null;
    let edges: Float32Array | null = null;
    try {
      const src = ctx.getImageData(0, 0, width, height);
      original = new Uint8ClampedArray(src.data);
      edges = generateSobelEdges(src);
    } catch {
      // Tainted canvas (cross-origin without CORS) — pixel reads are forbidden.
      // Fall back to the plain tier below: redraw the image every frame and paint
      // a translucent cyan band behind the laser. No reads, so tainting is fine.
      original = null;
      edges = null;
    }

    onSized?.();

    // ponytail: reduced-motion -> the clean image already drew above; no loop.
    if (prefersReducedMotion()) return;

    let scanY = 0;
    const tick = (): void => {
      if (stopped || canvas.isConnected === false) {
        stop();
        return;
      }
      scanY += SCAN_SPEED;
      if (scanY > height) scanY = 0;

      if (edges && original) {
        // Sobel tier: fresh frame, base restored from the captured original.
        const frame = ctx.createImageData(width, height);
        frame.data.set(original);
        const data = frame.data;

        // Only walk the wake band behind the laser line.
        const wakeTop = Math.max(0, Math.floor(scanY - WAKE));
        const wakeBottom = Math.min(height, Math.ceil(scanY));
        for (let y = wakeTop; y < wakeBottom; y++) {
          const fade = 1 - (scanY - y) / WAKE;
          const base = y * width;
          for (let x = 0; x < width; x++) {
            if (edges[base + x] <= EDGE_THRESHOLD) continue;
            const idx = (base + x) * 4;
            data[idx + 1] = Math.min(255, data[idx + 1] + 210 * fade);
            data[idx + 2] = Math.min(255, data[idx + 2] + 255 * fade);
          }
        }

        ctx.putImageData(frame, 0, 0);
      } else {
        // Plain tier: redraw the image, then tint the band behind the laser.
        ctx.drawImage(img, 0, 0, width, height);
        const wakeTop = Math.max(0, Math.floor(scanY - WAKE));
        const wakeBottom = Math.min(height, Math.ceil(scanY));
        const bandH = wakeBottom - wakeTop;
        if (bandH > 0) {
          ctx.fillStyle = 'rgba(0, 210, 255, 0.35)';
          ctx.fillRect(0, wakeTop, width, bandH);
        }
      }

      // HUD laser leading line, then reset shadow so later draws aren't blurred.
      ctx.strokeStyle = 'rgba(0, 210, 255, 0.8)';
      ctx.lineWidth = 2;
      ctx.shadowBlur = 8;
      ctx.shadowColor = '#00d2ff';
      ctx.beginPath();
      ctx.moveTo(0, scanY);
      ctx.lineTo(width, scanY);
      ctx.stroke();
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  };

  img.src = imageUrl;

  return { stop };
}